/**
 * Single drawer footer: flow history, Standard apply/discard, close & save.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { HelpCircle, RotateCcw, UploadCloud } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { Tooltip } from '@/components/ui/Tooltip';

import { SnapshotsButton } from './snapshots';
import type { FlowOptimizerProvider } from './services/flow-optimizer-api';
import {
  useFlowOptimizerDrawerFooter,
  type FlowOptimizerSaveAction,
} from './FlowOptimizerDrawerFooterContext';

export interface FlowOptimizerDrawerFooterBarProps {
  flowId: string;
  provider?: FlowOptimizerProvider;
  onClose: () => void;
  onSaveConfig: () => void;
  savePending: boolean;
  /** Save persists TrafficPulse-only config (exclusions, formula, etc.); show in CUSTOM (TP Optimisation) mode. */
  showSaveConfig: boolean;
  /** When set, Save is blocked (e.g. weight totals ≠ 100%); used as the disabled-button tooltip. */
  saveBlockedReason?: string;
}

const SAVE_CONFIG_HELP =
  'Persists the TrafficPulse Optimisation settings (formula, rules, ' +
  'clamps, schedule, Standard tab draft) to our database ONLY. ' +
  'Nothing is sent to Voluum or Binom — the live tracker stays untouched. ' +
  'Use the Apply button on the Preview tab when you want to push the ' +
  'optimizer\'s calculated weights upstream.';

const FlowOptimizerDrawerFooterBar: FC<FlowOptimizerDrawerFooterBarProps> = ({
  flowId,
  provider,
  onClose,
  onSaveConfig,
  savePending,
  showSaveConfig,
  saveBlockedReason,
}) => {
  const { slot } = useFlowOptimizerDrawerFooter();
  return (
    <div className="flex w-full flex-col gap-2">
      {slot?.applyNotice ? (
        <div
          role="alert"
          className="w-full rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2 text-xs text-destructive"
        >
          <p className="font-semibold text-destructive">{slot.applyNotice.title}</p>
          <ul className="mt-1.5 list-inside list-disc space-y-0.5 font-normal text-destructive/95">
            {slot.applyNotice.lines.map((line, i) => (
              <li key={`${i}-${line}`} className="break-words">
                {line}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2.5">
          <SnapshotsButton flowId={flowId} provider={provider} />
          {slot ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={slot.onDiscard}
                disabled={!slot.dirty}
                title="Revert Standard tab edits to last loaded flow"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                <span className="ml-1.5">Discard changes</span>
              </Button>
              {slot.actions.map((action) => (
                <SaveActionButton
                  key={action.id}
                  action={action}
                  applying={slot.applying}
                />
              ))}
            </>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} type="button" size="sm">
            Close
          </Button>
          {showSaveConfig ? (
            <span className="inline-flex items-center gap-1">
              <Button
                onClick={onSaveConfig}
                disabled={savePending || Boolean(saveBlockedReason)}
                loading={savePending}
                type="button"
                size="sm"
                title={
                  saveBlockedReason ??
                  'Save TrafficPulse Optimisation settings for this campaign'
                }
              >
                Save Config
              </Button>
              <Tooltip
                content={SAVE_CONFIG_HELP}
                side="top"
                interactive
                contentClassName="max-w-sm"
              >
                <HelpCircle
                  className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  aria-label="What Save Config does"
                />
              </Tooltip>
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

interface SaveActionButtonProps {
  action: FlowOptimizerSaveAction;
  applying: boolean;
}

const SaveActionButton: FC<SaveActionButtonProps> = ({ action, applying }) => {
  const buttonVariant = action.variant === 'secondary' ? 'outline' : undefined;
  return (
    <span className="inline-flex items-center gap-1">
      <Button
        type="button"
        size="sm"
        variant={buttonVariant}
        onClick={() => void action.onClick()}
        disabled={action.disabled || applying}
        loading={applying}
        title={action.helpText}
      >
        <UploadCloud className="h-3.5 w-3.5" aria-hidden />
        <span className="ml-1.5">{action.label}</span>
      </Button>
      {action.helpText ? (
        <Tooltip
          content={action.helpText}
          side="top"
          interactive
          contentClassName="max-w-sm"
        >
          <HelpCircle
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
            aria-label={`What ${action.label} does`}
          />
        </Tooltip>
      ) : null}
    </span>
  );
};

export default FlowOptimizerDrawerFooterBar;
